import { useEffect, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Button, Stack, Switch, Typography } from '@mui/material';

import { EntityMetadataModel, ParameterMetadata } from '../../../../api/api';
import { ExpressionModel } from '../../Models/ExpressionModel';
import { ExpressionTypes } from '../../Models/ExpressionTypes';
import { ObjectExpressionModel } from '../../Models/ObjectExpressionModel';
import { IVariableScope } from '../../Models/VariableScopes/IVariableScope';
import ExpressionObjectPlaceholder from './ExpressionObjectPlaceholder';
import ExpressionPlaceholder from './ExpressionPlaceholder';
import { shouldShowObjectEditor } from './shouldShowObjectEditor';

type ExpressionPlaceholderObjectInlineProps = {
	expression: ExpressionModel | undefined;
	onExpressionChange?: (expression: ExpressionModel | undefined) => void;
	parameterMetadata: ParameterMetadata;
	expectedObjectType?: EntityMetadataModel;
	variableScope?: IVariableScope;
	onSelectExpression?: React.MouseEventHandler<HTMLElement>;
};

export default function ExpressionPlaceholderObjectInline({
	expression,
	onExpressionChange,
	parameterMetadata,
	expectedObjectType,
	variableScope,
	onSelectExpression,
}: ExpressionPlaceholderObjectInlineProps) {
	const isProvided = expression !== null && expression !== undefined;
	const [isAccordionExpanded, setIsAccordionExpanded] = useState(isProvided);

	// Only show object placeholder if expression is undefined or is an Object type
	// Use consistent logic with other components
	const isObjectType = !!expectedObjectType;
	const expressionType = expression?.Type;
	const shouldShowObjectPlaceholder = shouldShowObjectEditor(expression, expressionType, isObjectType);

	useEffect(() => {
		if (!isProvided) setIsAccordionExpanded(false);
	}, [isProvided]);

	return (
		<Accordion expanded={isAccordionExpanded} onChange={(_, expanded) => isProvided && setIsAccordionExpanded(expanded)} disableGutters
			sx={{ bgcolor: 'background.default', color: 'text.primary', border: '1px solid', borderColor: 'divider', borderLeft: '3px solid', borderLeftColor: isProvided ? 'primary.main' : 'divider', boxShadow: 'none', borderRadius: '6px !important', '&:before': { display: 'none' } }}>
			<AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ flexDirection: 'row-reverse', gap: 1, minHeight: 44, bgcolor: 'background.paper', '& .MuiAccordionSummary-content': { my: 0.75 } }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" sx={{ width: '100%', minWidth: 0 }}>
					<Typography variant="body2" sx={{ fontWeight: 700, color: isProvided ? 'primary.main' : 'text.primary', overflowWrap: 'anywhere' }}>{expectedObjectType?.name ?? parameterMetadata.displayName ?? parameterMetadata.name}</Typography>
					<Stack direction="row" alignItems="center" spacing={0.5} onClick={(e) => e.stopPropagation()}>
						{onSelectExpression && (
							<Button component="span" size="small" onClick={onSelectExpression}>
								Use expression
							</Button>
						)}
						<Typography variant="caption" color="text.primary">
							Provide Value
						</Typography>
						<Switch
							size="small"
							inputProps={{ 'aria-label': `Provide value for ${parameterMetadata.displayName ?? parameterMetadata.name}` }}
							checked={isProvided}
							onChange={(e) => {
								if (e.target.checked) {
									onExpressionChange?.({ Type: ExpressionTypes.Object, Properties: {} } as ObjectExpressionModel);
									setIsAccordionExpanded(true);
								} else {
									onExpressionChange?.(undefined);
								}
							}}
						/>
					</Stack>
				</Stack>
			</AccordionSummary>
			{isProvided && (
				<AccordionDetails sx={{ p: 1, minWidth: 0 }}>
					{shouldShowObjectPlaceholder ? (
						<ExpressionObjectPlaceholder
							expression={expression as ObjectExpressionModel | undefined}
							onExpressionChange={onExpressionChange}
							variableScope={variableScope}
							expectedObjectType={expectedObjectType}
						/>
					) : (
						<ExpressionPlaceholder
							expression={expression}
							onExpressionChange={onExpressionChange}
							parameterMetadata={parameterMetadata}
							variableScope={variableScope}
							isEditable={true}
							expectedObjectType={expectedObjectType}
						/>
					)}
				</AccordionDetails>
			)}
		</Accordion>
	);
}
